// Decision-card consequences: applying an effect (cash / reputation / a multi-week
// demand swing) and landing the ones whose week has arrived. Kept standalone so
// both the reducer (immediate hit) and SettleWeek (delayed landing) can use it
// without a circular import.

import type { ConsequenceEffect, GameState } from '../state/types.js';
import { applyTxn } from '../ledger/append.js';

/** Apply one consequence effect to the state. `tag` flavours the ledger note. */
export function applyConsequenceEffect(state: GameState, effect: ConsequenceEffect, tag: string): GameState {
  let s = state;
  if (effect.cash) {
    s = applyTxn(s, {
      type: 'decision',
      amount: effect.cash,
      category: effect.cash >= 0 ? 'active' : 'expense',
      note: `Decision — ${tag}`,
    });
  }
  if (effect.reputation) {
    const reputation = Math.min(100, Math.max(0, s.profile.reputation + effect.reputation));
    s = { ...s, profile: { ...s.profile, reputation } };
  }
  if (effect.demandMult && effect.demandWeeks && effect.demandWeeks > 0) {
    s = {
      ...s,
      weeklyEffects: [
        ...s.weeklyEffects,
        {
          id: `decision-${tag}-${s.weeklyEffects.length}`,
          weeksRemaining: effect.demandWeeks,
          effects: [{ kind: 'demandMultiplier' as const, multiplier: effect.demandMult }],
        },
      ],
    };
  }
  return s;
}

/**
 * Land every pending consequence whose week has arrived: apply its effect, record
 * a landed-consequence recap for the UI, and drop it from the pending list.
 * Called inside SettleWeek for the settling week.
 */
export function landDueConsequences(state: GameState, settlingWeek: number): GameState {
  const pending = state.pendingConsequences ?? [];
  if (pending.length === 0) return state;

  const due = pending.filter((p) => p.landsWeek <= settlingWeek);
  if (due.length === 0) return state;

  let s = state;
  const landed = [...state.landedConsequences];
  for (const p of due) {
    s = applyConsequenceEffect(s, p.effect, p.framework);
    landed.push({ id: p.id, framework: p.framework, lesson: p.lesson, cash: p.effect.cash ?? 0 });
  }
  return {
    ...s,
    pendingConsequences: pending.filter((p) => p.landsWeek > settlingWeek),
    landedConsequences: landed,
  };
}
